import * as React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import {
  ApiError,
  type StudentImportPreviewResponse,
  type StudentImportSummary,
} from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { FirstRunShell } from "@/components/FirstRunShell";
import { buildFirstRunSteps, firstRunEyebrow } from "@/components/firstRunSteps";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import {
  DoneCard,
  PreviewCard,
  TemplateCard,
  UploadDropzone,
  MAX_FILE_BYTES,
  formatBytes,
  messageFor,
} from "@/components/student-imports";
import { useCompleteFirstRun } from "@/hooks/useFirstRun";
import {
  useStudentImportCommit,
  useStudentImportPreview,
  useStudentImportTemplateInfo,
} from "@/hooks/useStudentImports";
import { api } from "@/lib/api";

/**
 * Phase C1c — IA first-run wizard step 3: Import Students.
 *
 * Phase H6d — the dropzone, preview rows, summary card, and done CTA
 * card are now shared with the standing importer at `/imports/students`
 * (see `components/student-imports/`). This page keeps the wizard's
 * orchestration: re-entry guard, "Skip — start fresh" exit, and the
 * "Continue to dashboard" CTA that flips `tenant.first_run_complete`.
 *
 * UX flow (unchanged from C1c):
 *
 *  1. Empty state: download-template card + dropzone. The principal can
 *     either download the canonical CSV (and hand it to their office /
 *     vendor), or drag a file straight in.
 *  2. Preview state: server-validated row list with per-row verdict pills
 *     (`Fresh` / `Duplicate (email)` / `Duplicate (composite)` /
 *     `Duplicate in upload` / `Invalid row`). Summary card on the right
 *     surfaces totals + a primary "Import N students" CTA.
 *  3. Done state: success card with imported count + "Continue to
 *     dashboard" CTA. The wizard completes here (flips
 *     `tenant.first_run_complete=true`).
 *
 * Skip path: the shell's "Skip & finish setup" pill flips
 * first_run_complete and lands on /dashboard. We also surface a
 * page-level "Skip — start fresh" so the principal isn't forced to
 * scroll back up to the rail.
 *
 * Wizard re-entry guard: post-completion, this URL bounces to
 * /dashboard so it isn't a re-import hatch (the proper surface is
 * `/imports/students` introduced in H6d).
 */
export function FirstRunImportStudentsPage() {
  const navigate = useNavigate();
  const { firstRunComplete, status } = useAuth();

  // SC Foundation (F1) — study centres import "learners", not
  // "students". The step rail + header + CTA copy de-school off the
  // resolved vertical; school / coaching are unchanged.
  const { vertical } = useEnabledModules();
  const resolvedVertical = vertical ?? "school";
  const isStudyCentre = resolvedVertical === "study_centre";
  const steps = buildFirstRunSteps(resolvedVertical);
  const personMany = isStudyCentre ? "learners" : "students";

  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<StudentImportPreviewResponse | null>(null);
  const [topLevelError, setTopLevelError] = React.useState<string | null>(null);
  const [doneSummary, setDoneSummary] = React.useState<{
    summary: StudentImportSummary;
    batch_id: string;
  } | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const templateQuery = useStudentImportTemplateInfo();
  const previewMutation = useStudentImportPreview();
  const commitMutation = useStudentImportCommit();
  const completeMutation = useCompleteFirstRun();

  const [templateDownloadError, setTemplateDownloadError] = React.useState<string | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = React.useState(false);

  /**
   * Phase E1d — bug #5. The template endpoint requires a Bearer token, so a
   * plain `<a href>` (which doesn't carry our in-memory access token) was
   * 401'ing with `Missing or malformed Authorization header`. Use the API
   * client's interceptor-equipped axios instance to fetch the bytes as a
   * Blob, then synthesise a click on a temporary anchor to trigger the
   * browser download. This keeps the BE auth model intact (the endpoint
   * still requires a tenant-scoped token + slug) without exposing a public
   * download URL.
   */
  const handleDownloadTemplate = React.useCallback(async () => {
    setTemplateDownloadError(null);
    setIsDownloadingTemplate(true);
    try {
      const response = await api.raw.get<Blob>("/me/first-run/students/template.csv", {
        responseType: "blob",
      });
      const blob = response.data;
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = `${personMany}-template.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      setTemplateDownloadError(
        apiErr?.message ?? (err instanceof Error ? err.message : "Failed to download template."),
      );
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, [personMany]);

  // Wizard re-entry guard.
  if (status === "authed" && firstRunComplete === true) {
    return <Navigate to="/dashboard" replace />;
  }

  const resetSelection = () => {
    setFile(null);
    setPreview(null);
    setTopLevelError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFile = async (chosen: File) => {
    setTopLevelError(null);
    setPreview(null);
    if (chosen.size > MAX_FILE_BYTES) {
      setTopLevelError(
        `File is ${formatBytes(chosen.size)} — max 1 MB. Split into smaller batches.`,
      );
      return;
    }
    if (chosen.size === 0) {
      setTopLevelError("That file is empty. Pick a CSV with at least a header row.");
      return;
    }
    setFile(chosen);
    try {
      const result = await previewMutation.mutateAsync({ file: chosen });
      setPreview(result);
    } catch (err) {
      setTopLevelError(messageFor(err));
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    if (chosen) {
      void handleFile(chosen);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) {
      void handleFile(dropped);
    }
  };

  const onCommit = async () => {
    if (!file) return;
    setTopLevelError(null);
    try {
      // skip_duplicates=true is the partial-success path we want here:
      // the principal sees the dup-list in the preview, so committing
      // means "import the fresh ones, ignore the duplicates."
      const result = await commitMutation.mutateAsync({
        file,
        skipDuplicates: true,
      });
      setDoneSummary({ summary: result.summary, batch_id: result.batch_id });
    } catch (err) {
      setTopLevelError(messageFor(err));
    }
  };

  const onContinueToDashboard = () => {
    if (isStudyCentre) {
      navigate("/first-run/comms", { replace: true });
      return;
    }
    completeMutation.mutate(undefined, {
      onSuccess: () => {
        setTimeout(() => navigate("/dashboard", { replace: true }), 200);
      },
    });
  };

  const onSkipStartFresh = () => {
    if (isStudyCentre) {
      navigate("/first-run/comms", { replace: true });
      return;
    }
    completeMutation.mutate(undefined, {
      onSuccess: () => {
        setTimeout(() => navigate("/dashboard", { replace: true }), 200);
      },
    });
  };

  return (
    <FirstRunShell
      steps={steps}
      activeStep="import_students"
      eyebrow={firstRunEyebrow(resolvedVertical, "import_students")}
    >
      <header className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 grid gap-3 motion-safe:duration-500">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          Import your existing {personMany}
        </h1>
        <p className="text-muted-foreground max-w-xl text-sm leading-relaxed sm:text-base">
          Upload a CSV with your current {isStudyCentre ? "learner" : "student"} roster. We&apos;ll
          preview every row and flag duplicates before anything is saved. You can also start fresh
          and add {personMany} later from the Admissions area.
        </p>
      </header>

      {doneSummary ? (
        <DoneCard
          summary={doneSummary.summary}
          onContinue={onContinueToDashboard}
          continueLabel={isStudyCentre ? "Continue setup" : "Continue to dashboard"}
          isPending={completeMutation.isPending}
        />
      ) : preview ? (
        <PreviewCard
          file={file}
          preview={preview}
          template={templateQuery.data ?? null}
          onCommit={onCommit}
          onReset={resetSelection}
          isCommitting={commitMutation.isPending}
        />
      ) : (
        <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 mt-8 grid gap-6 motion-safe:duration-700">
          <TemplateCard
            template={templateQuery.data ?? null}
            onDownload={handleDownloadTemplate}
            isDownloading={isDownloadingTemplate}
            downloadError={templateDownloadError}
            bodyCopy={
              <>
                Use the column headers we recognise out of the box. Hand the file to your office /
                IT vendor and import their filled-in version below.
              </>
            }
          />
          <UploadDropzone
            fileInputRef={fileInputRef}
            isPreviewPending={previewMutation.isPending}
            onFileInputChange={onFileInputChange}
            onDrop={onDrop}
            inputId="first-run-student-import-file"
          />
        </div>
      )}

      {topLevelError ? (
        <div
          role="alert"
          className="border-destructive/50 bg-destructive/5 text-destructive mt-6 flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{topLevelError}</span>
        </div>
      ) : null}

      {!doneSummary ? (
        <div className="motion-safe:animate-in motion-safe:fade-in mt-10 grid gap-3 motion-safe:duration-700">
          <Button
            type="button"
            size="lg"
            variant="ghost"
            onClick={onSkipStartFresh}
            disabled={completeMutation.isPending}
            className="text-muted-foreground hover:text-foreground mx-auto"
          >
            {completeMutation.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Finishing setup…
              </>
            ) : (
              `Skip — I'll add ${personMany} later`
            )}
          </Button>
        </div>
      ) : null}
    </FirstRunShell>
  );
}
